/**
 * Utility functions for home data processing
 */

import type { HomeDetailsData } from '../types/intelligence.js';

export type { HomeDetailsData };

/**
 * Extracts address from home details JSON string
 * @param details - JSON string containing home details
 * @returns address string or empty string if not found
 */
export function extractAddressFromDetails(details: string | null): string {
  if (!details) return '';

  try {
    const parsed: HomeDetailsData = JSON.parse(details);
    return parsed.address || '';
  } catch (error) {
    // If details is not JSON, it might be a plain address string
    return details || '';
  }
}

/**
 * Checks if a home has a valid address specified
 * @param details - JSON string or plain string containing home details
 * @returns true if valid address exists, false otherwise
 */
export function hasValidAddress(details: string | null | undefined): boolean {
  const address = extractAddressFromDetails(details || null);
  if (!address) return false;

  // Check if address has meaningful content (not just whitespace)
  const trimmed = address.trim();
  if (trimmed.length === 0) return false;

  // Basic validation - address should have at least a number and some letters
  // Examples: "123 Main St", "456 Oak Avenue Apt 2B"
  const hasNumber = /\d/.test(trimmed);
  const hasLetters = /[a-zA-Z]/.test(trimmed);

  return hasNumber && hasLetters && trimmed.length >= 5;
}

/**
 * Parses home details from JSON string
 * @param details - JSON string containing home details
 * @returns parsed home details object
 */
export function parseHomeDetails(details: string | null): HomeDetailsData {
  if (!details) return {};

  try {
    return JSON.parse(details) as HomeDetailsData;
  } catch (error) {
    // If details is not JSON, return empty object
    return {};
  }
}

/**
 * Formats square footage for display
 * @param sqft - square footage number
 * @returns formatted string
 */
export function formatSquareFeet(sqft: number | null | undefined): string {
  if (!sqft) return 'N/A';
  return new Intl.NumberFormat('en-US').format(sqft) + ' sq ft';
}

/**
 * Formats price for display
 * @param price - price number
 * @returns formatted currency string
 */
export function formatPrice(price: number | null | undefined): string {
  if (!price) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
}